'use client'

import { useEffect, useMemo, useState } from 'react'

import { Search, X } from 'lucide-react'

import type { Advert } from '@/core/api/entities/campaign/advert'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePaginateAdverts } from '@/core/hooks/useAdvert'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface SelectExistingAdProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	selectedAdvertIds?: number[]
	/**
	 * Called with the adverts selected in this modal.
	 */
	onConfirmSelection?: (selectedAdverts: Advert[]) => void
}

export function SelectExistingAdModal({
	open,
	onOpenChange,
	selectedAdvertIds = [],
	onConfirmSelection,
}: SelectExistingAdProps) {
	const { t } = useTranslation('campaigns')
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 767px)').matches
	)

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)
		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	const advertsConfig = useAccountConfig({
		params: {
			per_page: 100,
		},
	})

	const { data: advertsResponse, isLoading } = usePaginateAdverts({
		config: advertsConfig,
		options: {
			enabled: open,
		},
	})

	const adverts = advertsResponse?.data || []

	const filteredAdverts = useMemo(() => {
		if (!searchQuery.trim()) return adverts
		const q = searchQuery.toLowerCase()
		return adverts.filter((ad) => ad.name.toLowerCase().includes(q))
	}, [adverts, searchQuery])

	useEffect(() => {
		if (open) {
			setSelectedIds(selectedAdvertIds)
		}
	}, [open, selectedAdvertIds])

	const toggleSelection = (id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		)
	}

	const handleConfirm = () => {
		if (!onConfirmSelection) {
			onOpenChange(false)
			return
		}

		const selected = adverts.filter((ad) => selectedIds.includes(ad.id))
		onConfirmSelection(selected)
		onOpenChange(false)
	}

	const handleClose = (openState: boolean) => {
		if (!openState) {
			setSearchQuery('')
			setSelectedIds([])
		}
		onOpenChange(openState)
	}

	const content = (
		<>
			<div className="px-5 pt-2 pb-3 flex items-center justify-between">
				{isDesktop ? (
					<h2 className="text-xl font-semibold">
						{t('modals.selectAds.title')}
					</h2>
				) : (
					<BottomSheetTitle className="text-xl font-semibold">
						{t('modals.selectAds.title')}
					</BottomSheetTitle>
				)}
				{!isDesktop && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={() => handleClose(false)}
						className="h-9 w-9 rounded-full"
					>
						<X className="w-5 h-5" />
					</Button>
				)}
			</div>

			<Separator />

			<div className="px-5 py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
				<div className="relative flex-none mb-3">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t('modals.selectAds.searchPlaceholder')}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10 h-10"
					/>
				</div>

				<ScrollArea className="h-[220px] lg:h-[320px] flex-1 md:flex-none overflow-y-auto border rounded-md">
					{isLoading ? (
						<div className="flex items-center justify-center h-full text-xs text-muted-foreground">
							{t('modals.selectAds.loading')}
						</div>
					) : filteredAdverts.length === 0 ? (
						<div className="flex items-center justify-center h-full text-xs text-muted-foreground">
							{t('modals.selectAds.noAds')}
						</div>
					) : (
						<ul className="divide-y">
							{filteredAdverts.map((advert) => (
								<li
									key={advert.id}
									className="flex items-center justify-between px-4 py-3 hover:bg-accent/40 cursor-pointer"
									onClick={() => toggleSelection(advert.id)}
								>
									<div className="flex-col flex">
										<span className="text-sm font-medium truncate max-w-[220px]">
											{advert.name}
										</span>
										<span className="text-[11px] text-muted-foreground">
											ID {advert.id}
										</span>
									</div>
									<Checkbox
										checked={selectedIds.includes(advert.id)}
										onCheckedChange={() => toggleSelection(advert.id)}
										onClick={(e) => e.stopPropagation()}
									/>
								</li>
							))}
						</ul>
					)}
				</ScrollArea>
			</div>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={handleClose}>
				<DialogContent className="max-w-lg p-0 gap-0 flex flex-col max-h-[90vh]">
					<DialogHeader className="sr-only">
						<DialogTitle>{t('modals.selectAds.title')}</DialogTitle>
					</DialogHeader>
					{content}
					<Separator />
					<DialogFooter className="px-6 py-4 flex flex-row justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => handleClose(false)}
							className="h-10 rounded-full px-6"
						>
							{t('modals.selectAds.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleConfirm}
							className="h-10 rounded-full px-6"
							disabled={selectedIds.length === 0}
						>
							{t('modals.selectAds.confirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={handleClose}>
			<BottomSheetContent
				variant="card"
				className="border-border h-[86vh] max-h-[92vh] px-0 pb-4"
				aria-describedby={undefined}
			>
				{content}
				<div className="px-5 pt-2">
					<Button
						type="button"
						onClick={handleConfirm}
						className="h-12 rounded-full w-full"
						disabled={selectedIds.length === 0}
					>
						{t('modals.selectAds.confirm')}
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	)
}
